import { MenuItem } from '@mui/material';
import MuiSelect, { SelectChangeEvent, SelectProps } from '@mui/material/Select';
import FieldWrapper from './wrappers/field-wrapper';
import { ISelectOptions } from './types';

interface IProps {
	label: string;
	description?: string;
	placeholder?: string;
	id: string;
	name?: string;
	options: ISelectOptions[];
	value?: string;
	hasError?: boolean;
	errorText?: string;
	onChange?: (e: SelectChangeEvent) => void;
	rest?: SelectProps;
}

/**
 * Select component for rendering a dropdown menu, with a FieldWrapper for error handling and accessibility purposes.
 * @param props - Props for the Select component.
 * @param {string} props.id - The id for the select input - mandatory for accessibility purposes.
 * @param {string} props.name - The name for the select input
 * @param {string} props.label - The label for the select input - populates a `FieldWrapper` label element with an aria-labelledby property linked to `id`.
 * @param {string} props.description - The description for the select input -  displayed below the label; populates a `FieldWrapper` description, with an aria-describedby property linked to `id`.
 * @param {Array} props.options - The options for the select input.
 * @param {string} props.placeholder - The placeholder for the select input - renders above the options on click to guide the user.
 * @param {string} props.value - The value for the select input.
 * @param {boolean} props.hasError - Whether the select input has an error.
 * @param {string} props.errorText - The error text for the select input.
 * @param {function} props.onChange - The onChange handler for the select input.
 * @param {object} props.rest - Additional props for the select input - spread directly to the `MuiSelect` component.
 * @returns The MUI `Select` component, wrapped in a `FieldWrapper`.
 */
const SelectField = ({
	label,
	description,
	placeholder,
	id,
	name,
	options,
	value,
	hasError,
	errorText,
	onChange,
	...rest
}: IProps) => {
	return (
		<FieldWrapper label={label} description={description} linkedInputId={id} hasError={hasError} helperText={errorText}>
			<MuiSelect
				inputProps={{ id, name }}
				labelId={`${id}-label`}
				aria-describedby={`${id}-description`}
				value={value ?? ''}
				color='secondary'
				fullWidth
				onChange={onChange}
				{...rest}
			>
				{placeholder && (
					<MenuItem key='disabled-select' disabled value=''>
						<em>{placeholder}</em>
					</MenuItem>
				)}
				{options.length &&
					options.map((x: ISelectOptions) => (
						<MenuItem key={x.label} value={x.value}>
							{x.label}
						</MenuItem>
					))}
			</MuiSelect>
		</FieldWrapper>
	);
};

export default SelectField;
